import { useErrorInlineNotificationAPI } from '@/components/ErrorInlineNotification/ErrorInlineNotification';
import {
  ApInvoiceTransactionStatusEnum,
  useBulkUpdateReorderTransactionItem,
  useUpdateReorderTransactionItem,
} from '@/context/services/transaction_items/List/TransactionItems.service';
import { TransactionItemsColumn } from '@/features/transaction_items/List/type';
import { useErrorTrackingSystem } from '@/hooks/useErrorTrackingSystem';
import { useTranslation } from '@/i18n';
import { useBooleanFlagValue } from '@openfeature/react-sdk';
import { useCallback } from 'react';
import { useParams } from 'react-router-dom';

type Props = {
  handlerSuccess: (successMsg: string) => void;
  handlerError: (errorMsg: string) => void;
  showLoading: (loading: boolean) => void;
};

export const useSubmitUpdateReorderTransactionItem = ({
  handlerSuccess,
  handlerError,
  showLoading,
}: Props) => {
  const { report_type_id } = useParams();
  const { mutateAsync: legacyMutateAsync } = useUpdateReorderTransactionItem(
    report_type_id ?? ''
  );
  const { mutateAsync: bulkMutateAsync } = useBulkUpdateReorderTransactionItem(
    report_type_id ?? ''
  );
  const { t } = useTranslation();
  const { send } = useErrorTrackingSystem();
  const setErrorNotification = useErrorInlineNotificationAPI();
  const isCustomTransactionRefactoringFlag = useBooleanFlagValue(
    'custom-transaction-refactoring',
    false
  );

  const onSubmitReorder = useCallback(
    async (items: TransactionItemsColumn[]): Promise<void> => {
      try {
        showLoading(true);

        if (isCustomTransactionRefactoringFlag) {
          const resp = await bulkMutateAsync({
            data: {
              transaction_items: items.map((item, index) => ({
                id: item.id,
                type: item.type ?? 'basic',
                priority: index,
              })),
            },
          });

          if (resp.status === ApInvoiceTransactionStatusEnum.SUCCESS) {
            handlerSuccess(t('transaction_items.update_reorder.success'));
          } else if (
            resp.status === ApInvoiceTransactionStatusEnum.INVALID_ERROR
          ) {
            handlerError(t('transaction_items.update_reorder.error'));
          } else if (
            resp.status === ApInvoiceTransactionStatusEnum.AUTH_ERROR
          ) {
            location.href = resp.redirectPath;
          } else if (
            resp.status === ApInvoiceTransactionStatusEnum.PROXY_LOGIN_ERROR
          ) {
            setErrorNotification(new Error(resp.errors.messages?.join('\n')));
          } else {
            handlerError(t('transaction_items.update_reorder.error'));
          }
        } else {
          const resp = await legacyMutateAsync({
            apReportTypeId: report_type_id ?? '',
            data: {
              ap_invoice_transaction_general_input_ordered_ids: items.map(
                (item) => item.id
              ),
            },
          });

          if (resp.status === ApInvoiceTransactionStatusEnum.SUCCESS) {
            handlerSuccess(t('transaction_items.update_reorder.success'));
          } else if (
            resp.status === ApInvoiceTransactionStatusEnum.INVALID_ERROR
          ) {
            handlerError(t('transaction_items.update_reorder.error'));
          } else if (
            resp.status === ApInvoiceTransactionStatusEnum.AUTH_ERROR
          ) {
            location.href = resp.redirectPath;
          } else if (
            resp.status === ApInvoiceTransactionStatusEnum.PROXY_LOGIN_ERROR
          ) {
            setErrorNotification(new Error(resp.errors.messages?.join('\n')));
          } else {
            handlerError(t('transaction_items.update_reorder.error'));
          }
        }
      } catch (error) {
        if (error instanceof Error && error?.name === 'AxiosError') {
          send(error.message);
        } else {
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          send(error as Error);
        }
        handlerError(t('transaction_items.update_reorder.error'));
      } finally {
        showLoading(false);
      }
    },
    [
      showLoading,
      isCustomTransactionRefactoringFlag,
      bulkMutateAsync,
      handlerSuccess,
      t,
      legacyMutateAsync,
      report_type_id,
      handlerError,
      setErrorNotification,
      send,
    ]
  );

  return { onSubmitReorder };
};
